export const DEMO_PERIODS = [
  { id: '1d', label: 'Last day', days: 1 },
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
];

export const DEMO_STATIONS = [
  { id: 'WS-01', name: 'POSTE 1', zone: 'Assembly cell A', targetPerDay: 39, owner: 'Team Alpha' },
  { id: 'WS-02', name: 'POSTE 2', zone: 'Assembly cell B', targetPerDay: 42, owner: 'Team Bravo' },
  { id: 'WS-03', name: 'POSTE 3', zone: 'Assembly cell C', targetPerDay: 37, owner: 'Team Charlie' },
  { id: 'WS-04', name: 'POSTE 4', zone: 'Assembly cell D', targetPerDay: 40, owner: 'Team Delta' },
];

export const DEMO_PRODUCTS = [
  { id: 'gmt', name: 'GMT Master', mix: 0.43, color: '#4164FD' },
  { id: 'royal', name: 'Royal Oak', mix: 0.34, color: '#4CBEBB' },
  { id: 'nautilus', name: 'Nautilus', mix: 0.23, color: '#F28725' },
];

export const DEMO_DEFECT_TYPES = [
  { id: 'missing', name: 'Missing component', weight: 0.23, severity: 'High', description: 'A required part was not fitted before validation.' },
  { id: 'alignment', name: 'Incorrect alignment', weight: 0.21, severity: 'Medium', description: 'A component is outside the assembly alignment tolerance.' },
  { id: 'sequence', name: 'Assembly step skipped', weight: 0.19, severity: 'High', description: 'The standard sequence of operations was not respected.' },
  { id: 'torque', name: 'Torque deviation', weight: 0.16, severity: 'High', description: 'The tightening operation deviated from the required standard.' },
  { id: 'scratch', name: 'Surface scratch', weight: 0.12, severity: 'Low', description: 'A visible mark was found during visual inspection.' },
  { id: 'component', name: 'Wrong component', weight: 0.09, severity: 'Medium', description: 'The installed component differs from the expected reference.' },
];

// Financial inputs are illustrative and deliberately editable for sales demonstrations.
export const DEMO_ASSUMPTIONS = {
  minutesSavedPerWatch: 4.8,
  hourlyLaborCost: 38,
  avoidedCostPerAtRiskWatch: 180,
  baselineDefectRate: 6.1,
  softwareCostPerStationDay: 280,
  annualWorkingDays: 235,
};

export const DEMO_CONFIG = {
  plantName: 'Precision Watch Manufacturing',
  workshopName: 'Head watch assembly',
  currency: 'EUR',
  historyDays: 180,
};

const getDateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const random = (seed) => {
  const value = Math.sin(seed * 127.1 + 81.39) * 43758.5453;
  return value - Math.floor(value);
};

const pickDefect = (seed) => {
  const selection = random(seed);
  let cumulative = 0;

  return DEMO_DEFECT_TYPES.find((defect) => {
    cumulative += defect.weight;
    return selection < cumulative;
  }) ?? DEMO_DEFECT_TYPES[0];
};

const getEventTime = (dateKey, shift, seed) => {
  const ranges = { A: [7, 14], B: [15, 22], C: [0, 6] };
  const [startHour, endHour] = ranges[shift];
  const hour = startHour + Math.floor(random(seed + 4) * (endHour - startHour + 1));
  const minute = Math.floor(random(seed + 8) * 60);
  const second = Math.floor(random(seed + 12) * 60);
  return `${dateKey} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
};

const buildMockData = () => {
  const reference = new Date();
  reference.setHours(12, 0, 0, 0);
  const daily = [];
  const events = [];

  for (let index = 0; index < DEMO_CONFIG.historyDays; index++) {
    const date = new Date(reference);
    date.setDate(reference.getDate() - (DEMO_CONFIG.historyDays - index - 1));
    const dayOfWeek = date.getDay();
    const dateKey = getDateKey(date);
    const progress = index / (DEMO_CONFIG.historyDays - 1);
    const isProductionDay = dayOfWeek !== 0 && dayOfWeek !== 6;

    DEMO_STATIONS.forEach((station, stationIndex) => {
      DEMO_PRODUCTS.forEach((product, productIndex) => {
        if (!isProductionDay) return;

        const seed = (index + 1) * 101 + (stationIndex + 1) * 37 + (productIndex + 1) * 13;
        const utilization = 0.87 + progress * 0.16 + (random(seed) - 0.5) * 0.12 + Math.sin(index / 7 + stationIndex) * 0.035;
        const assembled = Math.max(0, Math.round(station.targetPerDay * product.mix * utilization));
        const planned = Math.round(station.targetPerDay * product.mix * 1.035);
        const shift = ['A', 'B', 'C'][(index + stationIndex + productIndex) % 3];
        const defectRate = 0.062 - progress * 0.029 + stationIndex * 0.0018 + productIndex * 0.0015;
        const confirmedDefects = Math.max(0, Math.round(assembled * defectRate + (random(seed + 1) - 0.5) * 1.25));
        const falsePositives = random(seed + 2) < assembled * (0.006 - progress * 0.0042) ? 1 : 0;
        const missedDefects = random(seed + 9) < assembled * (0.0015 - progress * 0.0009) ? 1 : 0;
        const availability = 0.969 + progress * 0.024 - (random(seed + 5) * 0.006);
        const scheduledMinutes = 480;
        const uptimeMinutes = Math.min(scheduledMinutes, Math.round(scheduledMinutes * availability));
        const responseMs = Math.round(185 - progress * 65 + random(seed + 6) * 22);
        let corrected = 0;

        for (let eventIndex = 0; eventIndex < confirmedDefects + falsePositives; eventIndex++) {
          const isFalsePositive = eventIndex >= confirmedDefects;
          const eventSeed = seed * 31 + eventIndex * 17;
          const defect = isFalsePositive ? null : pickDefect(eventSeed);
          const isCorrected = !isFalsePositive && random(eventSeed + 7) < 0.76 + progress * 0.13;
          if (isCorrected) corrected += 1;

          events.push({
            id: `EVT-${dateKey.replace(/-/g, '')}-${stationIndex + 1}${productIndex + 1}-${String(eventIndex + 1).padStart(2, '0')}`,
            date: dateKey,
            timestamp: getEventTime(dateKey, shift, eventSeed),
            stationId: station.id,
            productId: product.id,
            shift,
            defectId: defect?.id ?? 'false_positive',
            defectName: defect?.name ?? 'False positive',
            severity: defect?.severity ?? 'Low',
            status: isFalsePositive ? 'Dismissed' : isCorrected ? 'Corrected' : 'Rejected',
            confidence: isFalsePositive ? 0.65 + random(eventSeed + 9) * 0.22 : 0.91 + random(eventSeed + 9) * 0.085,
          });
        }

        daily.push({
          date: dateKey,
          stationId: station.id,
          productId: product.id,
          shift,
          planned,
          assembled,
          confirmedDefects,
          falsePositives,
          missedDefects,
          corrected,
          rejected: confirmedDefects - corrected,
          scheduledMinutes,
          uptimeMinutes,
          responseMs,
          softwareCost: DEMO_ASSUMPTIONS.softwareCostPerStationDay * product.mix,
        });
      });
    });
  }

  events.sort((a, b) => b.timestamp.localeCompare(a.timestamp) || b.id.localeCompare(a.id));
  return { daily, events, referenceDate: getDateKey(reference) };
};

export const PERCEPTION_DEMO_DATA = buildMockData();
